import { useQuery, UseQueryResult } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { AIToolsUsage } from "api/resources/aiTools/types"
import {
  SubscriptionDetails,
  SuraasaPlusSubscriptionStatus,
} from "api/resources/suraasaPlus/types"
import { context } from "global/Context/context"
import { createContext, useContext, useEffect, useState } from "react"
import { useMatch, useNavigate } from "react-router-dom"
import routes, { routeBuilder } from "routes"
import { SURAASA_PLUS_START_FREE_TRIAL_EVENT_NAME } from "utils/constants"
import { sleep } from "utils/helpers"
import { trackingService } from "utils/tracking"

import FeatureOverviewDialog from "../Dialogs/FeatureOverviewDialog"
import SelectYourPlanDialog from "../Dialogs/SelectYourPlanDialog"
import WelcomeToSuraasaPlus from "../Dialogs/WelcomeSuraasaPlusDialog"
import GenerationLimitReachedDialog from "../GenerationLimitReached/Dialog"
import { canGenerateMore, isSubscriptionActive } from "./helper"

function getJobApplicationsAllowed(
  used?: number,
  remaining?: number | null
): number | undefined {
  if (used === undefined || remaining === undefined || remaining === null) {
    return undefined
  }
  return used + remaining
}

type Context = {
  subscriptionDetails: SubscriptionDetails | undefined
  refreshSubscriptionData: () => Promise<void>
  initiateSuraasaPlusPurchaseFlow: {
    (): Promise<void>
    (skipFeatureOverview: boolean): Promise<void>
  }
  claimFreeTrial: (args?: { showWelcomeDialog?: boolean }) => Promise<void>
  aiToolGenerationsUsed: number | undefined
  aiToolGenerationsAllowed: number | undefined
  jobApplicationsUsed: number | undefined
  jobApplicationsAllowed: number | undefined
  checkGenerationAvailability: (cb: () => Promise<void>) => void
  isActive: boolean | undefined
  isOverdue: boolean | undefined
  isExpired: boolean | undefined
  isCancelled: boolean | undefined
  isEnded: boolean | undefined
  subscriptionDetailsQuery: UseQueryResult<SubscriptionDetails> | undefined
  aiToolsUsageQuery: UseQueryResult<AIToolsUsage> | undefined
  canGenerate: boolean | undefined
  remainingGenerations: number | undefined
  isFreeTrialAvailable: boolean | undefined
  getSuraasaPlusNewYear: () => void
  isSPNYUser?: boolean
}

const SuraasaPlusContext = createContext<Context>({
  subscriptionDetails: undefined,
  refreshSubscriptionData: async () => {
    throw new Error("refreshSubscriptionData not implemented")
  },
  initiateSuraasaPlusPurchaseFlow: async () => {
    throw new Error("initiateSuraasaPlusPurchaseFlow not implemented")
  },
  claimFreeTrial: async () => {
    throw new Error("claimFreeTrial not implemented")
  },
  aiToolGenerationsUsed: undefined,
  aiToolGenerationsAllowed: undefined,
  jobApplicationsUsed: undefined,
  jobApplicationsAllowed: undefined,
  checkGenerationAvailability: () => {
    throw new Error("checkGenerationAvailability not implemented")
  },
  isActive: undefined,
  isExpired: undefined,
  isEnded: undefined,
  isCancelled: undefined,
  isOverdue: undefined,
  subscriptionDetailsQuery: undefined,
  aiToolsUsageQuery: undefined,
  canGenerate: undefined,
  remainingGenerations: undefined,
  isFreeTrialAvailable: undefined,
  getSuraasaPlusNewYear: () => {},
  isSPNYUser: undefined,
})

export const SuraasaPlusAccessProvider = (props: {
  children: React.ReactNode
}) => {
  const { isAuthenticated } = useContext(context)
  const navigate = useNavigate()
  const isAlreadyOnPaymentPage = Boolean(useMatch(routes.subscription))

  const [
    openGenerationLimitReachedDialog,
    setOpenGenerationLimitReachedDialog,
  ] = useState(false)

  const [openFeatureOverviewDialog, setOpenFeatureOverviewDialog] = useState<{
    open: boolean
    handleClose: () => void
    onProceed: () => Promise<void>
  }>({
    open: false,
    handleClose: () => {},
    onProceed: async () => {},
  })

  const [openSelectPlanDialog, setOpenSelectPlanDialog] = useState<{
    open: boolean
    handleClose: (selectedProductSlug?: string) => void
  }>({
    open: false,
    handleClose: () => {},
  })

  const [openWelcomeToSuraasaPlusDialog, setOpenWelcomeToSuraasaPlusDialog] =
    useState(false)

  const params = {
    fields: ["transactions"],
  }

  const subscriptionDetailsQuery = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.suraasaPlus.retrieveSubscription(params).queryKey,
    queryFn: () =>
      api.suraasaPlus.retrieveSubscription({
        params: params,
      }),
  })

  const aiToolsUsageQuery = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.aiTools.retrieveAiToolsUsage().queryKey,
    queryFn: () => api.aiTools.retrieveAiToolsUsage(),
  })
  const jobApplicationUsageQuery = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.jobs.jobApplicationUsage().queryKey,
    queryFn: () => api.jobs.getJobApplicationUsage(),
  })

  const jobApplicationsUsed = jobApplicationUsageQuery.data?.usageConsumed
  const remainingJobApplications = jobApplicationUsageQuery.data?.usageRemaining

  const jobApplicationsAllowed = getJobApplicationsAllowed(
    jobApplicationsUsed,
    remainingJobApplications
  )

  const ruleExecutionDetailsQuery = useQuery({
    enabled: isAuthenticated,
    queryKey: queries.suraasaPlus.retrieveRuleExecutionDetails({
      urlParams: {
        eventName: SURAASA_PLUS_START_FREE_TRIAL_EVENT_NAME,
      },
    }).queryKey,
    queryFn: () =>
      api.suraasaPlus.retrieveRuleExecutionDetails({
        urlParams: {
          eventName: SURAASA_PLUS_START_FREE_TRIAL_EVENT_NAME,
        },
      }),
  })

  const aiToolGenerationsUsed = aiToolsUsageQuery.data?.consumptionCount
  const aiToolGenerationsAllowed = aiToolsUsageQuery.data?.consumptionAllowed

  const isActive = isSubscriptionActive(subscriptionDetailsQuery.data)

  const isOverdue =
    subscriptionDetailsQuery.data?.subscription.status ===
    SuraasaPlusSubscriptionStatus.paymentOverdue

  const canGenerate = canGenerateMore(
    aiToolGenerationsUsed,
    aiToolGenerationsAllowed
  )
  const isSPNYUser =
    subscriptionDetailsQuery?.data?.subscription.metaData?.isNy26

  const remainingGenerations =
    aiToolGenerationsUsed !== undefined &&
    aiToolGenerationsAllowed !== undefined
      ? Math.max(0, aiToolGenerationsAllowed - aiToolGenerationsUsed)
      : undefined

  const freeTrialAvailable = false

  useEffect(() => {
    if (isSPNYUser != undefined) {
      trackingService.setPostHogUserAttribute({
        is_sp_ny26: isSPNYUser,
      })
    }
  }, [isSPNYUser])

  useEffect(() => {
    if (isActive != undefined) {
      trackingService.setPostHogUserAttribute({
        sp_subscription_active: isActive,
      })
    }
  }, [isActive])

  const getSuraasaPlusNewYear = () => {
    navigate(routes.suraasaPlus.payment)
  }

  const showFeatureOverviewDialog = (onProceed: () => Promise<void>) => {
    return new Promise<void>(resolve => {
      setOpenFeatureOverviewDialog({
        open: true,
        onProceed: async () => {
          await onProceed()
          setOpenFeatureOverviewDialog({
            open: false,
            handleClose: () => {},
            onProceed: async () => {},
          })
          resolve()
        },
        handleClose: () => {
          setOpenFeatureOverviewDialog({
            open: false,
            handleClose: () => {},
            onProceed: async () => {},
          })
          resolve()
        },
      })
    })
  }

  const showSelectPlanDialog = () => {
    return new Promise<string | undefined>(resolve => {
      setOpenSelectPlanDialog({
        open: true,
        handleClose: result => {
          setOpenSelectPlanDialog({ open: false, handleClose: () => {} })
          resolve(result)
        },
      })
    })
  }

  async function initiateSuraasaPlusPurchaseFlow(
    skipFeatureOverview?: boolean
  ) {
    // if (freeTrialAvailable) {
    //   await showFeatureOverviewDialog(async () => {
    //     await claimFreeTrial()
    //     setOpenFeatureOverviewDialog({
    //       open: false,
    //       handleClose: () => {},
    //       onProceed: async () => {},
    //     })
    //   })
    // } else {
    //   if (skipFeatureOverview) {
    //     initiatePlanSelectionAndPurchaseFlow()
    //   } else {
    //     await showFeatureOverviewDialog(async () => {
    //       // Close sheet if already open
    //       setOpenFeatureOverviewDialog({
    //         open: false,
    //         handleClose: () => {},
    //         onProceed: async () => {},
    //       })
    //       initiatePlanSelectionAndPurchaseFlow()
    //     })
    //   }
    // }
    if (skipFeatureOverview) {
      initiatePlanSelectionAndPurchaseFlow()
    } else {
      await showFeatureOverviewDialog(async () => {
        // Close sheet if already open
        setOpenFeatureOverviewDialog({
          open: false,
          handleClose: () => {},
          onProceed: async () => {},
        })
        initiatePlanSelectionAndPurchaseFlow()
      })
    }
  }

  async function initiatePlanSelectionAndPurchaseFlow() {
    const selectedProductSlug = await showSelectPlanDialog()
    if (selectedProductSlug) {
      navigate(
        routeBuilder(
          routes.subscription,
          {},
          {
            product: selectedProductSlug,
          }
        ),
        {
          replace: isAlreadyOnPaymentPage,
        }
      )
    }
  }

  async function refreshSubscriptionData() {
    try {
      await subscriptionDetailsQuery.refetch()
      await aiToolsUsageQuery.refetch()
      await ruleExecutionDetailsQuery.refetch()
    } catch (error) {
      console.error("Failed to refetch subscription data: ", error)
    }
  }

  async function claimFreeTrial({
    showWelcomeDialog = true,
  }: { showWelcomeDialog?: boolean } = {}) {
    try {
      await api.suraasaPlus.startFreeTrial({
        data: { ruleSlug: SURAASA_PLUS_START_FREE_TRIAL_EVENT_NAME },
      })
      await sleep(3000)
      await refreshSubscriptionData()
      if (showWelcomeDialog) {
        setOpenWelcomeToSuraasaPlusDialog(true)
      }
    } catch (e) {
      console.log("Failed to claim suraasa plus free trial: ", e)
    }
  }

  const checkGenerationAvailability = async (cb: () => Promise<void>) => {
    if (canGenerate) {
      try {
        await cb()
      } catch {
        // error is handled by the mutation's onError callback
      }
      return
    }
    if (!canGenerate) {
      setOpenGenerationLimitReachedDialog(true)
      return
    }
    return
  }

  return (
    <SuraasaPlusContext.Provider
      value={{
        refreshSubscriptionData,
        claimFreeTrial,
        initiateSuraasaPlusPurchaseFlow,
        aiToolGenerationsUsed,
        aiToolGenerationsAllowed,
        jobApplicationsUsed,
        jobApplicationsAllowed,
        aiToolsUsageQuery,
        checkGenerationAvailability,
        subscriptionDetailsQuery,
        subscriptionDetails: subscriptionDetailsQuery.data,
        isExpired:
          subscriptionDetailsQuery.data?.subscription?.status ===
          SuraasaPlusSubscriptionStatus.expired,
        isEnded:
          subscriptionDetailsQuery.data?.subscription &&
          subscriptionDetailsQuery.data.subscription.endDate !== null,
        isCancelled:
          subscriptionDetailsQuery.data?.subscription?.status ===
          SuraasaPlusSubscriptionStatus.cancelled,
        isActive,
        isOverdue,
        canGenerate,
        remainingGenerations,
        isFreeTrialAvailable: freeTrialAvailable,
        getSuraasaPlusNewYear,
        isSPNYUser,
      }}
    >
      <GenerationLimitReachedDialog
        open={openGenerationLimitReachedDialog}
        handleClose={() => setOpenGenerationLimitReachedDialog(false)}
      />
      <WelcomeToSuraasaPlus
        open={openWelcomeToSuraasaPlusDialog}
        handleClose={() => setOpenWelcomeToSuraasaPlusDialog(false)}
      />
      <FeatureOverviewDialog
        open={openFeatureOverviewDialog.open}
        handleClose={openFeatureOverviewDialog.handleClose}
        onClick={openFeatureOverviewDialog.onProceed}
      />
      <SelectYourPlanDialog
        open={openSelectPlanDialog.open}
        handleClose={openSelectPlanDialog.handleClose}
      />
      {props.children}
    </SuraasaPlusContext.Provider>
  )
}

export const useSuraasaPlusContext = () => {
  const context = useContext(SuraasaPlusContext)
  if (!context) {
    throw new Error(
      "useSuraasaPlusContext must be used within a SuraasaPlusContextProvider"
    )
  }
  return context
}
